// TAB: TAKEOFF — editable grid + Line Inspector drawer.
import { useCallback, useMemo, useState } from "react";
import ConfirmDialogue from "../../../../../../../components/ui/ConfirmDialogue";
import NoDataState from "../../../../../components/NoDataState";
import Skeleton from "../../../../../../../components/ui/Skeleton";
import { useWorkspace } from "../../../../hooks/useEstimateWorkspace";
import { getLineDefaults } from "../../../../engine/calcMock";
import { COLUMNS, buildOptions } from "./takeoff-columns";
import TakeoffGrid from "./TakeoffGrid";
import TakeoffToolbar from "./TakeoffToolbar";
import LineInspector from "./LineInspector";

const GROUPS_KEY = "sb_estimate_takeoff_groups";
const DEFAULT_GROUPS = { inputs: true, quantities: true, hours: true, costs: true };

const loadGroups = () => {
  try {
    return { ...DEFAULT_GROUPS, ...JSON.parse(localStorage.getItem(GROUPS_KEY) || "{}") };
  } catch {
    return DEFAULT_GROUPS;
  }
};

const isOverridden = (line, defaults) =>
  Object.keys(defaults).some((k) => line[k] !== defaults[k]);

export default function TakeoffTab() {
  const ws = useWorkspace();
  const { draft, calc, config, idx, isLocked, selectedLineId, setSelectedLineId } = ws;

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [groups, setGroups] = useState(loadGroups);
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const options = useMemo(() => buildOptions(config), [config]);
  const columns = useMemo(() => COLUMNS.filter((c) => c.group === "frozen" || groups[c.group]), [groups]);

  const resultsById = useMemo(() => Object.fromEntries((calc?.results || []).map((r) => [r.id, r])), [calc]);
  const defaultsById = useMemo(
    () => Object.fromEntries(draft.lines.map((l) => [l.id, getLineDefaults(l, config, draft.project, idx)])),
    [draft.lines, draft.project, config, idx]
  );

  const counts = useMemo(
    () => ({
      all: draft.lines.length,
      errors: draft.lines.filter((l) => resultsById[l.id] && !resultsById[l.id].valid).length,
      overridden: draft.lines.filter((l) => isOverridden(l, defaultsById[l.id] || {})).length,
    }),
    [draft.lines, resultsById, defaultsById]
  );

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return draft.lines.filter((l) => {
      if (filter === "errors" && !(resultsById[l.id] && !resultsById[l.id].valid)) return false;
      if (filter === "overridden" && !isOverridden(l, defaultsById[l.id] || {})) return false;
      if (!q) return true;
      return [l.area, l.rawName, idx.surfaces[l.surfaceId]?.name].some((v) => v?.toLowerCase().includes(q));
    });
  }, [draft.lines, search, filter, resultsById, defaultsById, idx]);

  const toggleGroup = (key) =>
    setGroups((g) => {
      const next = { ...g, [key]: !g[key] };
      try {
        localStorage.setItem(GROUPS_KEY, JSON.stringify(next));
      } catch {
        // per-viewer preference only
      }
      return next;
    });

  const onRowClick = useCallback(
    (lineId) => {
      setSelectedLineId(lineId);
      setInspectorOpen(true);
    },
    [setSelectedLineId]
  );

  const selectedLine = draft.lines.find((l) => l.id === selectedLineId) || null;
  const selectedRowNumber = selectedLine ? rows.findIndex((l) => l.id === selectedLine.id) + 1 : 0;

  if (!calc) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-8 w-full" />
        <Skeleton.Rows rows={8} />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-[420px] flex-col gap-2">
      <TakeoffToolbar
        search={search}
        onSearch={setSearch}
        filter={filter}
        onFilter={setFilter}
        counts={counts}
        groups={groups}
        onToggleGroup={toggleGroup}
        locked={isLocked}
        hasSelection={!!selectedLine}
        onAdd={() => ws.addLine(selectedLineId)}
        onDuplicate={() => selectedLine && ws.duplicateLine(selectedLine.id)}
        onDelete={() => setConfirmDelete(true)}
        inspectorOpen={inspectorOpen}
        onToggleInspector={() => setInspectorOpen((o) => !o)}
      />

      <div className="flex min-h-0 flex-1 gap-2">
        <div className="min-w-0 flex-1">
          {rows.length === 0 ? (
            <div className="flex h-full items-center justify-center rounded-[4px] border border-border bg-surface">
              <NoDataState
                icon="search"
                title={draft.lines.length ? "No lines match" : "No takeoff lines"}
                message={draft.lines.length ? "Clear the filter to see all lines." : "Add a line to start building this estimate."}
                showBack={false}
                showRefresh={false}
                showCreate={!draft.lines.length && !isLocked}
                createText="Add line"
                onCreate={() => ws.addLine(null)}
                size="small"
              />
            </div>
          ) : (
            <TakeoffGrid
              rows={rows}
              columns={columns}
              resultsById={resultsById}
              defaultsById={defaultsById}
              options={options}
              totals={calc.totals}
              locked={isLocked}
              selectedLineId={selectedLineId}
              onActiveLineChange={setSelectedLineId}
              onRowClick={onRowClick}
              onEscape={() => setInspectorOpen(false)}
              actions={ws}
            />
          )}
        </div>
        {inspectorOpen && (
          <LineInspector
            line={selectedLine}
            result={selectedLine ? resultsById[selectedLine.id] : null}
            defaults={selectedLine ? defaultsById[selectedLine.id] : null}
            rowNumber={selectedRowNumber}
            config={config}
            idx={idx}
            project={draft.project}
            onClose={() => setInspectorOpen(false)}
          />
        )}
      </div>

      {confirmDelete && selectedLine && (
        <ConfirmDialogue
          confirmData={{
            title: "Delete this line?",
            message: `"${selectedLine.rawName}" will be removed from the estimate. You can undo with Ctrl+Z.`,
            confirmText: "Delete line",
            variant: "danger",
          }}
          handleConfirmClose={(ok) => {
            setConfirmDelete(false);
            if (ok) ws.deleteLines([selectedLine.id]);
          }}
        />
      )}
    </div>
  );
}
